import {useLoginMutation, useRegisterMutation} from "../../services/api/authApi.js";
import {Form, FormControl, FormField, FormItem, FormLabel, FormMessage} from "../ui/form.tsx";
import {z} from "zod";
import {useForm} from "react-hook-form";
import {Input} from "../ui/input.tsx";
import {zodResolver} from "@hookform/resolvers/zod";
import {Button} from "../ui/button.tsx";
import {useNavigate} from "react-router-dom";
import PublicAuthLayout from "./PublicAuthLayout.jsx";
import { Loader2 } from "lucide-react";

export const SignUpCard = () => {
    const navigate = useNavigate();
    const [register, {isLoading}] = useRegisterMutation();
    const [login, {isLoading: loginIsLoading }] = useLoginMutation();

    const signUpFormSchema = z.object({
        email: z.string().email( {message: 'Introduce un correo válido'}),
        password: z.string().min(8, {message: 'La contraseña debe tener al menos 8 caracteres'}),
        first_name: z.string().min(1, {message: 'Ingresa tu nombre'}),
        last_name: z.string().min(1, {message: 'Ingresa tu apellido'}),
    }).superRefine(({ password }, checkPassComplexity) => {
        const containsUppercase = (ch: string) => /[A-Z]/.test(ch);
        const containsLowercase = (ch: string) => /[a-z]/.test(ch);
        const containsSpecialChar = (ch: string) =>
            /[`!@#$%^&*()_\-+=\[\]{};':"\\|,.<>\/?~ ]/.test(ch);
        let countOfUpperCase = 0,
            countOfLowerCase = 0,
            countOfNumbers = 0,
            countOfSpecialChar = 0;
        for (let i = 0; i < password.length; i++) {
            const ch = password.charAt(i);
            if (!isNaN(+ch)) countOfNumbers++;
            else if (containsUppercase(ch)) countOfUpperCase++;
            else if (containsLowercase(ch)) countOfLowerCase++;
            else if (containsSpecialChar(ch)) countOfSpecialChar++;
        }
        if (
            countOfLowerCase < 1 ||
            countOfUpperCase < 1 ||
            countOfNumbers < 1
        ) {
            checkPassComplexity.addIssue({
                code: "custom",
                path: ["password"],
                message: "Usa al menos una mayúscula, una minúscula y un número",
            });
        }
    });

    const form = useForm({
        resolver: zodResolver(signUpFormSchema),
        defaultValues: {
            email: '',
            first_name: '',
            last_name: '',
            password: '',
        }
    })

    function onSubmit(data) {
        const payload = {
            email: data.email,
            password: data.password,
            name: `${data.first_name} ${data.last_name}`,
            number: "0000000000",
            role: 'user'
        };
        register(payload).then((res: any) => {
            if (res.data) {
                login({email: form.getValues('email'), password: form.getValues('password')}).then((res: any) => {
                    if (res.data) {
                        navigate("/reservar")
                    }
                })
            }
        })
    }

    return (
        <PublicAuthLayout>
            <div className="text-center mb-8">
                <h1 className="text-3xl font-black text-[#3D1A20] font-serif uppercase tracking-tight mb-2">Crear Cuenta</h1>
                <p className="text-[#3D1A20]/70 text-sm font-medium">Registra tus datos para acceder al sistema</p>
            </div>

            <Form {...form} >
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5">
                    <FormField
                        control={form.control}
                        name="email"
                        render={({field}) => (
                            <FormItem>
                                <FormLabel className="text-[#3D1A20] font-bold text-xs uppercase tracking-wider">Correo electrónico</FormLabel>
                                <FormControl>
                                    <Input 
                                        placeholder="tu@email.com" 
                                        className="bg-[#F3ECE7] border-[#E5D8CC] text-[#3D1A20] placeholder:text-[#3D1A20]/40 focus-visible:ring-[#3D1A20] focus-visible:border-[#3D1A20] rounded-xl px-4 py-3 h-auto shadow-inner" 
                                        {...field} 
                                    />
                                </FormControl>
                                <FormMessage className="text-red-500 text-xs" />
                            </FormItem>
                        )}
                    />
                    <div className="grid grid-cols-2 gap-4">
                        <FormField
                            control={form.control}
                            name="first_name"
                            render={({field}) => (
                                <FormItem>
                                    <FormLabel className="text-[#3D1A20] font-bold text-xs uppercase tracking-wider">Nombre</FormLabel>
                                    <FormControl>
                                        <Input 
                                            placeholder="Juan" 
                                            className="bg-[#F3ECE7] border-[#E5D8CC] text-[#3D1A20] placeholder:text-[#3D1A20]/40 focus-visible:ring-[#3D1A20] focus-visible:border-[#3D1A20] rounded-xl px-4 py-3 h-auto shadow-inner" 
                                            {...field} 
                                        />
                                    </FormControl>
                                    <FormMessage className="text-red-500 text-xs" />
                                </FormItem>
                            )}
                        />
                        <FormField
                            control={form.control}
                            name="last_name"
                            render={({field}) => (
                                <FormItem>
                                    <FormLabel className="text-[#3D1A20] font-bold text-xs uppercase tracking-wider">Apellido</FormLabel>
                                    <FormControl>
                                        <Input 
                                            placeholder="Pérez" 
                                            className="bg-[#F3ECE7] border-[#E5D8CC] text-[#3D1A20] placeholder:text-[#3D1A20]/40 focus-visible:ring-[#3D1A20] focus-visible:border-[#3D1A20] rounded-xl px-4 py-3 h-auto shadow-inner" 
                                            {...field} 
                                        />
                                    </FormControl>
                                    <FormMessage className="text-red-500 text-xs" />
                                </FormItem>
                            )}
                        />
                    </div>
                    <FormField
                        control={form.control}
                        name="password"
                        render={({field}) => (
                            <FormItem>
                                <FormLabel className="text-[#3D1A20] font-bold text-xs uppercase tracking-wider">Contraseña</FormLabel>
                                <FormControl>
                                    <Input 
                                        type="password" 
                                        placeholder="••••••••" 
                                        className="bg-[#F3ECE7] border-[#E5D8CC] text-[#3D1A20] placeholder:text-[#3D1A20]/40 focus-visible:ring-[#3D1A20] focus-visible:border-[#3D1A20] rounded-xl px-4 py-3 h-auto shadow-inner tracking-widest" 
                                        {...field} 
                                    />
                                </FormControl>
                                <FormMessage className="text-red-500 text-xs" />
                            </FormItem>
                        )}
                    />

                    <Button type="submit" className="w-full bg-[#3D1A20] hover:bg-[#2E1318] text-[#F3ECE7] font-bold py-6 rounded-xl mt-4 transition-all shadow-xl hover:shadow-[#3D1A20]/30 hover:-translate-y-0.5 uppercase tracking-widest text-xs" disabled={isLoading || loginIsLoading}>
                        {(isLoading || loginIsLoading) ? <Loader2 className="animate-spin mr-2" size={18} /> : null}
                        {(isLoading || loginIsLoading) ? "Procesando..." : "Registrarse"}
                    </Button>

                    <div className="mt-6 text-center space-y-3 flex flex-col items-center">
                        <Button variant="link" type="button" className="text-[#3D1A20]/70 hover:text-[#3D1A20] font-bold text-xs uppercase tracking-wider" onClick={() => navigate("/login")}>
                            ¿Ya tienes una cuenta? Inicia sesión
                        </Button>
                        <Button variant="link" type="button" className="text-[#3D1A20]/40 hover:text-[#3D1A20] font-bold text-[10px] uppercase tracking-wider" onClick={() => navigate("/signup-admin")}>
                            Crear cuenta de Administrador
                        </Button>
                    </div>
                </form>
            </Form>
        </PublicAuthLayout>
    )
}
